import { z } from "zod";
import { STREAMING_SERVICES, type StreamingService } from "./constants";

const callbackSchema = z.object({
  integrationUserUUID: z.string().uuid().nullable(),
  authModel: z.object({
    uuid: z.string().uuid(),
    status: z.enum(["initialised", "error", "success"]),
    error: z.string().nullish(),
  }),
  integration: z.object({
    type: z.enum(STREAMING_SERVICES),
    returnUrl: z.string().url(),
  }),
});

export type ImportAuthCode =
  | "user_cancelled"
  | "browser_dismissed"
  | "provider_denied"
  | "session_expired"
  | "invalid_callback"
  | "provider_mismatch"
  | "provider_error"
  | "browser_busy"
  | "browser_unavailable"
  | "auth_url_failed"
  | "browser_failed"
  | "import_start_failed";

export function classifyImportAuthError(error: unknown): ImportAuthCode {
  const message = (
    error instanceof Error ? error.message : String(error)
  ).toLowerCase();
  if (/access_denied|access denied|user_denied/.test(message))
    return "provider_denied";
  if (/expired|invalid_grant/.test(message)) return "session_expired";
  if (/already open|invalid state with a redirect/.test(message))
    return "browser_busy";
  if (/no matching browser/.test(message)) return "browser_unavailable";
  return "provider_error";
}

export function parseImportCallback(
  data64: string | undefined,
  service?: StreamingService,
) {
  try {
    if (!data64) return { ok: false, code: "invalid_callback" } as const;
    // URLSearchParams decodes '+' to a space if the provider did not escape base64.
    const data = callbackSchema.parse(
      JSON.parse(atob(data64.replace(/ /g, "+"))),
    );
    if (service && data.integration.type !== service) {
      return { ok: false, code: "provider_mismatch" } as const;
    }
    if (data.authModel.status !== "success") {
      return {
        ok: false,
        code: classifyImportAuthError(
          data.authModel.error ?? data.authModel.status,
        ),
      } as const;
    }
    if (!data.integrationUserUUID)
      return { ok: false, code: "invalid_callback" } as const;
    return {
      ok: true,
      service: data.integration.type,
      integrationUserUUID: data.integrationUserUUID,
    } as const;
  } catch {
    return { ok: false, code: "invalid_callback" } as const;
  }
}

export function callbackDataFromUrl(
  url: string,
  returnUrl: string,
): string | undefined {
  try {
    const actual = new URL(url);
    const expected = new URL(returnUrl);
    if (
      actual.protocol !== expected.protocol ||
      actual.host !== expected.host ||
      actual.pathname !== expected.pathname
    )
      return undefined;
    return actual.searchParams.get("data64") ?? undefined;
  } catch {
    return undefined;
  }
}

export const importAuthMessages: Record<ImportAuthCode, string> = {
  user_cancelled:
    "Connection cancelled. You can try again or pick your music genres.",
  browser_dismissed:
    "The connection window closed before we received a result. Try again.",
  provider_denied:
    "Permission wasn’t granted. Try again and allow access to import your playlists.",
  session_expired: "Your connection session expired. Try again to sign in.",
  invalid_callback:
    "We couldn’t read the connection result. Try connecting again.",
  provider_mismatch:
    "The connection returned from a different service. Please try again.",
  provider_error:
    "Your streaming service couldn’t complete the connection. Please try again.",
  browser_busy: "Another connection is still open. Close it, then try again.",
  browser_unavailable:
    "No browser is available. Install or enable a browser, then try again.",
  auth_url_failed:
    "We couldn’t start the connection. Check your connection and try again.",
  browser_failed: "We couldn’t open the connection window. Please try again.",
  import_start_failed:
    "You’re connected, but we couldn’t start your import. Try again.",
};
